import { useCallback, useMemo, useState } from 'react';
import { ResponsiveGridProps } from '@cbhq/cds-web';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextDisplay3, TextHeadline, TextTitle3 } from '@cbhq/cds-web/typography';

import { DashboardProvider } from ':cloud/contexts/DashboardProvider';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { EventName, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { useGetStakingRewards } from ':cloud/queries/EarningsQueries/useGetStakingRewards';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { Chart } from ':cloud/widgets/earnings/Chart';
import { RANGE } from ':cloud/widgets/earnings/config';
import { EarningsReport } from ':cloud/widgets/earnings/EarningsReport';
import { EarningsRange, ProtocolData } from ':cloud/widgets/earnings/types';
import { getLatestBalance, sumEarnings } from ':cloud/widgets/earnings/utils';
import { Layout } from ':cloud/widgets/layout';
import { CloudErrorBoundary } from ':cloud/widgets/sharedcomponents';

const responsiveGridConfig: ResponsiveGridProps = {
  phone: {
    columns: 1,
  },
  tablet: {
    columns: 1,
  },
  desktop: {
    columns: 1,
  },
};

const content = Object.entries(RANGE).map(([key, title]) => (
  <SelectOption borderRadius="rounded" key={key} value={key} title={title} />
));

const DEFAULT_PROTOCOL = 'ethereum';

function EarningsContent() {
  const { activeOrg } = useGetUser();
  const { isPhone } = useSimpleBreakpoints();
  const [range, setRange] = useState<EarningsRange>('currentMonth');
  const handleSwitchChartRange = useCallback(
    (value: EarningsRange) => {
      setRange(value);
    },
    [setRange],
  );
  const { isLoading, data } = useGetStakingRewards(
    { filter: range },
    activeOrg?.organizationId || '',
  );

  const filteredProtocolData: ProtocolData = useMemo(() => {
    const protocolReward = data.protocolRewards?.find((pr) => pr.protocol === DEFAULT_PROTOCOL);
    return {
      ticker: protocolReward?.ticker || '',
      rewards: protocolReward?.rewards || {},
      balances: protocolReward?.balances || {},
    };
  }, [data.protocolRewards]);

  const totalRewards = useMemo(() => {
    return sumEarnings(filteredProtocolData.rewards);
  }, [filteredProtocolData.rewards]);

  const latestBalance = useMemo(() => {
    return getLatestBalance(filteredProtocolData.balances);
  }, [filteredProtocolData.balances]);

  return (
    <VStack gap={10}>
      <VStack gap={3}>
        <HStack justifyContent="space-between" alignItems="center">
          <TextTitle3 as="h1">Earnings Summary</TextTitle3>

          <HStack alignItems="center">
            <Dropdown
              value={range}
              onChange={(value) => handleSwitchChartRange(value)}
              content={<VStack width={208}>{content}</VStack>}
              enableMobileModal
            >
              {isPhone ? (
                <IconButton name="more" />
              ) : (
                <Button compact endIcon="caretDown" variant="secondary">
                  {RANGE[range]}
                </Button>
              )}
            </Dropdown>
          </HStack>
        </HStack>

        <HStack justifyContent="flex-start" alignItems="center" gap={4}>
          <EarningsDetails
            title="Total gross rewards"
            value={totalRewards.totalGrossEarnedNativeUnits}
            unit={filteredProtocolData.ticker}
          />
          <EarningsDetails
            title="Total net rewards"
            value={totalRewards.totalEarnedNativeUnits}
            unit={filteredProtocolData.ticker}
          />
          <EarningsDetails
            title="Staked balance"
            value={latestBalance}
            unit={filteredProtocolData.ticker}
          />
        </HStack>

        <Grid responsiveConfig={responsiveGridConfig} gap={5}>
          <Chart
            key="EarningsChart"
            chartType="area"
            isLoading={isLoading}
            protocolData={filteredProtocolData}
            latestStakeBalance={latestBalance}
            totalGrossRewards={totalRewards.totalGrossEarnedNativeUnits}
            totalNetRewards={totalRewards.totalEarnedNativeUnits}
          />
        </Grid>

        <Grid responsiveConfig={responsiveGridConfig}>
          <EarningsReport />
        </Grid>
      </VStack>
    </VStack>
  );
}

interface EarningsDetailsProps {
  title: string;
  value: string;
  unit: string;
}

function EarningsDetails({ title, value, unit }: EarningsDetailsProps) {
  let text = value;
  if (value !== '--') {
    text += ` ${unit}`;
  }

  return (
    <Box bordered borderRadius="roundedSmall" display="flex" spacing={2} width="25%">
      <VStack spacing={2}>
        <TextHeadline as="p" color="foregroundMuted">
          {title}
        </TextHeadline>
        <TextDisplay3 as="h2">{text}</TextDisplay3>
      </VStack>
    </Box>
  );
}

export function EarningsPage() {
  useLogViewOnMount(EventName.earnings_page_view);
  return (
    <Layout>
      <Layout.MainContainer fullWidth>
        <CloudErrorBoundary name="EarningsContent">
          <DashboardProvider>
            <EarningsContent />
          </DashboardProvider>
        </CloudErrorBoundary>
      </Layout.MainContainer>
    </Layout>
  );
}
